/* eslint-disable max-len */
import {
  ClearRounded as Clear,
  ListAltOutlined,
  ChevronLeft,
  ChevronRight,
  ArrowBackIosNew,
} from "@mui/icons-material";
import { styled, TextField, useMediaQuery, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map, GeolocateControl, MapRef, Marker, ScaleControl } from "react-map-gl";
import { useLocation, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Row, PulseCircle } from "../../components/FlexComponents";
import ApiRoutes from "../../config/api.routes";
import { loadMapboxgl, MAPBOX_STYLE, MAPBOX_TOKEN } from "../../config/mapbox";
import { useAxios } from "../../services/http.service";
import { convertToParcels } from "../Scan";
import { getReadOnlyContract } from "../../services/contract.service";
import { LandCardPopup } from "../../components";
import { BOTTOM_NAV_HIDDEN_TRANSFORM } from "../../components/BottomNavBar";
import { getSaleDescription } from "../../components/LandCardPopup";
import LandPage from "../LandPage";
import { TERM_UNIT } from "../../utils/constants";
import { resolveStandardStatus } from "../../utils/standard-status";
import { landImageSrc } from "../../utils/landImage";
import { LAND_MAX_ZOOM, landFocusZoom } from "../../utils/landMeta";
import { TokenMeta } from "../../types/LandToken";

// ---------------- Marker → card "bloom" ----------------
// Clicking a land marker no longer swaps in a separate centered popup: the
// marker's inner shell morphs in place from the photo circle into the card
// (the photo is continuous through the morph, so it reads as the marker
// inflating), and the real LandCardPopup content staggers in on top. Geometry
// mirrors LandCardPopup: a 300×321 CardBox plus an optional 51px sale bar.
const BLOOM_CARD_W = 300;
const BLOOM_CARD_H = 321;
const BLOOM_SALE_BAR_H = 51; // LandCardPopup's price bar (its BUTTON_HEIGHT)
const BLOOM_GAP = 14; // clearance between the geo point and the card edge
const BLOOM_MS = 600;
// Close is quicker than open: the deflate should get out of the way, not
// replay the entrance in reverse.
const BLOOM_CLOSE_MS = 400;
const BLOOM_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
// The shell morph's transition list at a given duration (open vs close).
const bloomShellTransition = (ms: number) =>
  ["width", "height", "transform", "border-radius"].map((p) => `${p} ${ms}ms ${BLOOM_EASE}`).join(", ");
const THUMB_STRIP_H = 88; // ThumbnailStrip height — camera framing keeps the card clear of it
// Mobile only: above this many rendered markers, swap each marker's two infinite
// pulse rings for a static glow halo. Compositing dozens of animated ring layers
// every frame janks the map on phones once the field gets dense; a static halo
// costs nothing per frame, so smoothness stops scaling with marker count. Desktop
// keeps the live pulse regardless of count.
const MOBILE_DENSE_MARKER_LIMIT = 8;
// Location panel (flag / city / country / coords): reverse-geocoded only once the
// camera has stayed still this long, so a burst of wheel flicks or drag releases
// costs one Geocoding API request (billed per request) instead of one per moveend.
const LOCATION_SETTLE_MS = 500;

// /map rows carry meta.location as a JSON string; the map wants it parsed.
const parseMapLand = (item: any) => {
  const location = JSON.parse(item.meta.location);
  return {
    ...item,
    meta: { ...item.meta, location },
    search: { location, name: item.meta.name },
  };
};

// Idle pulse rings around closed markers (compositor-only: transform/opacity).
const bloomPulse = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
`;
// One-shot leaf-green ripple fired from the marker on click.
const bloomRipple = keyframes`
  0% { opacity: 0.9; transform: translate(-50%, -50%) scale(0.9); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(3.4); }
`;
// Card-content entrance, staggered per child while the shell finishes blooming.
// No blur: filter is paint-per-frame and runs on 4 concurrent children — too
// expensive on mobile. Opacity + translate stays on the compositor.
const bloomContentIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;
// Whole-content fade. An animation (not a transition) so it still plays when
// the content mounts with the open class already applied.
const bloomContentFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
// Single light sweep across the sale bar once it lands.
const bloomSweep = keyframes`
  to { transform: translateX(110%); }
`;

const Explore = () => {
  const axios = useAxios();
  const mapRef = useRef<MapRef>();
  const infoRef = useRef<any>();
  const moveTimeoutRef = useRef<NodeJS.Timeout>();
  // Centre last sent to the Geocoding API, at the precision the panel shows
  // (toFixed(6)): a camera that settles where it already was costs no request.
  const geocodedKeyRef = useRef<string | null>(null);
  const geocodeAbortRef = useRef<AbortController | null>(null);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [locationDetails, setLocationDetails] = useState<any>(null);
  const [countryFlag, setCountryFlag] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const routerLocation = useLocation();
  // Set by LandPage's "View on map" button. `fromLandPage` skips the fly-to;
  // `lon`/`lat` let the map mount already centered on the land so the globe
  // never flashes while the lands fetch is in flight.
  const landPageNavState = routerLocation.state as { fromLandPage?: boolean; lon?: number; lat?: number } | null;
  const cameFromLandPage = Boolean(landPageNavState?.fromLandPage);
  const landPageHasCoords =
    cameFromLandPage && Number.isFinite(landPageNavState?.lon) && Number.isFinite(landPageNavState?.lat);

  // Zoom the map settles at when focused on a single land (deep link, marker
  // open, search result) whose size is unknown — landZoom sizes the rest.
  const LAND_FOCUS_ZOOM = isMobile ? 14 : 14;
  // Screen span (px) a focused land's side should fill (see landFocusZoom). Mobile
  // asks for less so the parcel sits in some geographic context instead of filling
  // the small viewport with one tight satellite crop.
  const LAND_TARGET_PX = isMobile ? 160 : 240;
  // Focus zoom for one land: sized to its area, capped at LAND_MAX_ZOOM.
  const landZoom = (meta: TokenMeta | null | undefined, lat: number) =>
    landFocusZoom(meta, lat, LAND_TARGET_PX) ?? LAND_FOCUS_ZOOM;

  const initialViewState = {
    longitude: landPageHasCoords ? (landPageNavState!.lon as number) : 80.9425,
    latitude: landPageHasCoords ? (landPageNavState!.lat as number) : 7.1924,
    zoom: landPageHasCoords ? LAND_FOCUS_ZOOM : 2,
    bearing: 0,
    pitch: 0,
  };

  const [search, setSearch] = useState<string>("");
  const [allData, setAllData] = useState<any>([]);
  // Flips once the first /map fetch has settled (fulfilled or failed). The `?asset=` deep-link
  // effect below has to wait for the lands list, and `allData.length === 0` cannot tell "not
  // fetched yet" from "fetched, and the map has no lands" — on an empty map the second case
  // used to park the effect forever and the land never opened.
  const [landsLoaded, setLandsLoaded] = useState<boolean>(false);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const tokenInfoRef = useRef<any>(null);
  tokenInfoRef.current = tokenInfo;
  // Tracks the `?asset=` value we've already opened the inline detail for, so the
  // deep-link effect doesn't re-trigger on every render / unrelated param change.
  const handledAssetRef = useRef<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // Inline land-detail reveal (the full LandPage rendered over the live map).
  // Desktop: opened by clicking the popup. Mobile: shown whenever the popup is
  // open, so the user can scroll straight into the detail.
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [revealOrigin, setRevealOrigin] = useState<{ x: number; y: number } | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [sizeMax, setSizeMax] = useState(3333);
  const [currentZoom, setCurrentZoom] = useState<number>(initialViewState.zoom);
  // Tracks the last zoom category so handleZoomChange can skip state updates
  // (and the markers useMemo re-run) when the zoom moves within a category.
  const zoomCategoryRef = useRef<string>(
    initialViewState.zoom <= 5 ? "small" : initialViewState.zoom <= 10 ? "medium" : "large"
  );
  const [landViewMode, setLandViewMode] = useState<"active" | "live" | "all">("all");
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [headerHeight, setHeaderHeight] = useState<number>(140);
  const [searchBottom, setSearchBottom] = useState<number>(82);
  // Marker bloom: whether the open card grows downward from the point (set when
  // there's no room above the marker at click time — the ".below" clamp).
  const [openBelow, setOpenBelow] = useState<boolean>(false);
  // The open card's content stays mounted ~one transition past close so it can
  // fade out while the shell deflates (unmounting in lockstep would snap it).
  const [cardMountId, setCardMountId] = useState<any>(null);
  // Thumbnail-strip swap: the id the camera is currently flying to. Its popup
  // bloom is suppressed until the fly lands (moveend), so the strip flies to the
  // land first and only then blooms it. Marker clicks leave this null (their card
  // blooms immediately and rides the fly).
  const [flyingToId, setFlyingToId] = useState<any>(null);
  // Header-search results, best match first. While set, the thumbnail strip
  // shows only these (the map keeps every marker); cleared when the card closes.
  const [stripLands, setStripLands] = useState<any[] | null>(null);
  const stripData = useMemo(
    () =>
      stripLands ??
      [...(filteredData.length > 0 ? filteredData : allData)].sort(
        (a, b) => (a.etNft?.id ?? 0) - (b.etNft?.id ?? 0)
      ),
    [stripLands, filteredData, allData]
  );
  // Marker root the card bloomed from — for the ripple and for handing focus back.
  const openAnchorElRef = useRef<HTMLElement | null>(null);
  // Swallow the synthesized post-tap click that fires right after a marker tap on
  // mobile — without this it lands on MapDismiss and dismisses the just-opened card.
  const openGuardUntilRef = useRef(0);
  // The mounted card content (.ef-cc) — focus target.
  const openCardContentRef = useRef<HTMLDivElement | null>(null);
  // Mirror for stable reads from callbacks created before the header measured.
  const headerHeightRef = useRef(headerHeight);
  headerHeightRef.current = headerHeight;

  // Idle pulse rings run `infinite` on every marker, and each animated ring is
  // its own compositor layer — so a dense map paints dozens of them even when
  // most are panned off-screen. A single shared IntersectionObserver pauses the
  // rings on off-screen markers; only the handful actually visible keep
  // animating. Per-marker visibility is held in React state (see
  // PulseObservedAnchor) so a markers-memo re-render never clobbers it.
  // WeakMap (not Map — `Map` is the react-map-gl component here): keyed by the
  // anchor element so detached markers are reclaimed without manual cleanup.
  const pulseCallbacksRef = useRef<WeakMap<Element, (offscreen: boolean) => void>>(new WeakMap());
  const [pulseObserver] = useState<IntersectionObserver | undefined>(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return undefined;
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          pulseCallbacksRef.current.get(entry.target)?.(!entry.isIntersecting);
        });
      },
      // rootMargin gives a little lead-in so a pin resumes pulsing just before
      // it scrolls back into view rather than popping mid-animation.
      { root: null, rootMargin: "120px", threshold: 0 }
    );
  });
  useEffect(() => () => pulseObserver?.disconnect(), [pulseObserver]);

  // Track BottomNavBar top edge so search sits 18px above it (or 5vh on desktop)
  useEffect(() => {
    const updateSearchBottom = () => {
      const navEl = document.getElementById("eco-bottom-navbar");
      const isVisible = navEl && window.getComputedStyle(navEl).display !== "none";
      if (navEl && isVisible) {
        const rect = navEl.getBoundingClientRect();
        const distanceFromBottom = window.innerHeight - rect.top;
        setSearchBottom(Math.round(distanceFromBottom + 18));
      } else {
        setSearchBottom(Math.round(window.innerHeight * 0.05));
      }
    };

    updateSearchBottom();
    window.addEventListener("resize", updateSearchBottom);
    window.addEventListener("orientationchange", updateSearchBottom);

    const navEl = document.getElementById("eco-bottom-navbar");
    let ro: any = null;
    const RZ: any = (window as any).ResizeObserver;
    if (navEl && typeof RZ !== "undefined") {
      ro = new RZ(() => updateSearchBottom());
      ro.observe(navEl);
    }

    const interval = setInterval(updateSearchBottom, 1000);

    return () => {
      window.removeEventListener("resize", updateSearchBottom);
      window.removeEventListener("orientationchange", updateSearchBottom);
      if (ro && navEl) ro.unobserve(navEl);
      clearInterval(interval);
    };
  }, []);

  // Track header (including banners) height to offset map overlays
  useEffect(() => {
    const updateHeaderHeight = () => {
      const el = document.getElementById("eco-header");
      if (el) {
        const rect = el.getBoundingClientRect();
        setHeaderHeight(Math.max(0, Math.round(rect.bottom)));
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    const el = document.getElementById("eco-header");
    // Observe dynamic changes (e.g., banners appearing/disappearing)
    let ro: any = null;
    const RZ: any = (window as any).ResizeObserver;
    if (el && typeof RZ !== "undefined") {
      ro = new RZ(() => updateHeaderHeight());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      if (ro && el) {
        ro.unobserve(el);
      }
    };
  }, []);

  const handleZoomChange = useCallback(() => {
    if (!mapRef.current) return;
    const zoom = mapRef.current.getZoom();
    console.log("Map zoom:", zoom);
    // Only re-render markers when the zoom bucket changes (≤5 / 5-10 / >10).
    // onZoom fires every animation frame; updating state every frame forces the
    // markers useMemo to recompute all JSX on every frame → jitter.
    const cat = zoom <= 5 ? "small" : zoom <= 10 ? "medium" : "large";
    if (cat !== zoomCategoryRef.current) {
      zoomCategoryRef.current = cat;
      setCurrentZoom(zoom);
    }
  }, []);

  const toggleLandViewMode = useCallback(
    (mode: "active" | "live" | "all") => {
      // If clicking on the already active mode, switch to 'all'
      const newMode = landViewMode === mode ? "all" : mode;
      setLandViewMode(newMode);

      console.log(`Switched to ${newMode} lands view`);

      if (allData && allData.length > 0) {
        if (newMode === "active") {
          // Filter for active lands - those with easementActivated.active = true
          const activeData = allData.filter((item: any) => {
            const offerOpen = item.offerStatus === "opened";
            const active = item.etNft?.easementActivated && item.etNft.easementActivated.active === true;
            return offerOpen || active;
          });
          setFilteredData(activeData);
        } else if (newMode === "live") {
          // Filter for live lands - land is considered live if none of these conditions are true
          const liveData = allData.filter((token: any) => {
            const isSold = token.etNft?.status === "sold" || token.etNft?.etAuction?.status === "completed";
            const isExpired =
              isSold &&
              moment(token.etNft?.easementActivated?.contractStart)
                .add(token.meta?.contractPeriod, TERM_UNIT)
                .isBefore();
            const isMinted = token.etNft?.status === "minted" || token.etNft?.etAuction?.status === "ended";
            const offerOpen = token.offerStatus === "opened";
            const auction = !isSold && !isExpired && !isMinted;
            return auction || offerOpen;
          });
          setFilteredData(liveData);
        } else {
          // For 'all' mode, show all lands
          setFilteredData([]);
        }
      }
    },
    [landViewMode, allData]
  );

  // Location panel geocoding. Mapbox bills reverse geocoding per request and asks
  // for mapbox.places calls to be triggered by user activity, so only the settled
  // map centre is geocoded, only while the panel is on screen (it's hidden whenever
  // a card or the inline detail is open) and only once per distinct centre. Flights
  // to a land (marker, thumbnail strip, deep link, single search hit) therefore cost
  // nothing; closing the card flies back out and the panel refreshes then.
  const locationPanelVisible = !(isModalOpen || detailOpen);
  const locationPanelVisibleRef = useRef(locationPanelVisible);
  locationPanelVisibleRef.current = locationPanelVisible;
  const geocodeSettledCenter = useCallback(() => {
    const map = mapRef.current;
    // Mid-flight: the flight's own moveend reschedules this.
    if (!map || map.isMoving() || !locationPanelVisibleRef.current) {
      return;
    }
    const center = map.getCenter();
    fetchLocationDetails(center.lat, center.lng);
    // fetchLocationDetails only uses refs and state setters, so the first render's copy is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMapMove = useCallback(() => {
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current);
    }
    moveTimeoutRef.current = setTimeout(geocodeSettledCenter, LOCATION_SETTLE_MS);
  }, [geocodeSettledCenter]);
  // Panel (re)appearing — first mount, or a card / detail closing: refresh it for
  // wherever the camera settles.
  useEffect(() => {
    if (locationPanelVisible) {
      handleMapMove();
    }
  }, [locationPanelVisible, handleMapMove]);

  // Cleanup timeout and any in-flight geocode on unmount
  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
      geocodeAbortRef.current?.abort();
    };
  }, []);

  // Prevent body scroll on the full-screen map page
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  useEffect(() => {
    getLandSizeLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Camera helper for land-focused moves: frames the land so the bloomed card
  // has room — the marker lands below the header + thumbnail strip with the card
  // growing upward ("above" mode), or right under them with the card growing
  // downward ("below" mode). Offset = where the geo point ends up vs. screen center.
  // `meta` sizes the zoom to the land (landZoom); an explicit `zoom` wins.
  const flyToLand = (
    lon: number,
    lat: number,
    options?: { duration?: number; jump?: boolean; zoom?: number; meta?: TokenMeta | null }
  ) => {
    if (!mapRef.current) {
      return;
    }
    const topBound = headerHeightRef.current + THUMB_STRIP_H + 24;
    // On mobile the thumbnail strip is visible above the card, so add extra
    // clearance so the card top never butts up against the strip.
    const cardTopClearance = isMobile ? 48 : 0;
    // Bottom of the visible map area: above the bottom nav bar on mobile, or the
    // full viewport height on desktop.
    const navEl = document.getElementById("eco-bottom-navbar");
    const navVisible = navEl && window.getComputedStyle(navEl).display !== "none";
    const bottomBound = navVisible ? navEl.getBoundingClientRect().top : window.innerHeight;
    // Center the card in the visible map area (between header+strip and bottom nav).
    // markerY = mapAreaCenter + BLOOM_GAP + cardH/2 places the card mid-map.
    // Math.max clamps so the card top never clips behind the thumbnail strip.
    const mapAreaCenterY = (topBound + bottomBound) / 2;
    const centeredPointY = mapAreaCenterY + BLOOM_GAP + BLOOM_CARD_H / 2;
    const pointY = Math.max(topBound + BLOOM_CARD_H + BLOOM_GAP + cardTopClearance, centeredPointY);
    const camera = {
      zoom: options?.zoom ?? landZoom(options?.meta, lat),
      center: [lon, lat] as [number, number],
      offset: [0, Math.round(pointY - window.innerHeight / 2)] as [number, number],
    };
    if (options?.jump) {
      mapRef.current.jumpTo(camera);
    } else if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      // Reduced motion: mapbox would turn the flyTo into a jumpTo, which drops
      // `offset` and leaves the land dead-centre. An instant easeTo keeps it.
      mapRef.current.easeTo({ ...camera, duration: 0 });
    } else {
      mapRef.current.flyTo({ ...camera, duration: options?.duration ?? 3000 });
    }
  };

  // Run `fn` once the camera move flyToLand just started has landed. Under
  // reduced motion that move is instant and its moveend has already fired by
  // the time flyToLand returns, so a once("moveend") added now would wait
  // forever — only subscribe while the camera is actually still moving.
  const afterFlyLands = (fn: () => void) => {
    if (mapRef.current?.isMoving()) {
      mapRef.current.once("moveend", fn);
    } else {
      fn();
    }
  };

  // `?id=` / `?search=` are opened by the header-search effect below, once
  // landsLoaded flips.
  const fetchLands = async () => {
    try {
      const result = await axios.get(ApiRoutes.map.root).then((res) => res.data);
      setAllData(result.map(parseMapLand));
      setFilteredData([]); // Reset filtered data to trigger re-filtering based on current mode
    } catch (e: any) {
      console.log(e);
    } finally {
      setLandsLoaded(true);
    }
  };

  // Marker click → bloom. The card opens immediately and rides the marker
  // through the fly-to: it lives inside the marker element, so Mapbox keeps it
  // glued to the geo point — deliberately NOT closed on map movement.
  const handleMarkerClick = (item: any, anchorEl?: HTMLElement | null) => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (anchorEl) {
      openAnchorElRef.current = anchorEl;
      // One-shot click ripple — re-armed via reflow so a re-click restarts it.
      const ripple = anchorEl.querySelector<HTMLElement>(".ef-ripple");
      if (ripple && !reducedMotion) {
        ripple.classList.remove("ef-go");
        void ripple.offsetWidth;
        ripple.classList.add("ef-go");
      }
      // Surgical will-change: promote the bloom shell only for the transition
      // duration, then clear so the layer isn't kept alive while the card sits open.
      const bloom = anchorEl.querySelector<HTMLElement>(".ef-bloom");
      if (bloom && !reducedMotion) {
        bloom.style.willChange = "transform, width, height, border-radius";
        setTimeout(() => {
          bloom.style.willChange = "";
        }, BLOOM_MS + 50);
      }
    }
    setOpenBelow(false);
    // Marker click blooms immediately and rides the fly — never suppressed.
    setFlyingToId(null);
    setTokenInfo(item);
    setIsModalOpen(true);
    // Ignore dismissals for 600ms — long enough to outlast the phantom click.
    openGuardUntilRef.current = performance.now() + 600;

    if (mapRef && mapRef.current) {
      const lon = item.meta.location.lon;
      const lat = item.meta.location.lat;
      flyToLand(lon, lat, { duration: 600, meta: item.meta });
    }
  };

  // Strip the `?asset=` deep-link param so the URL reflects the visible state
  // (closing the detail returns to a clean `/explore`) and re-opening the same
  // land later re-triggers the deep-link effect.
  const clearAssetParam = useCallback(() => {
    handledAssetRef.current = null;
    if (!searchParams.has("asset")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("asset");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleModalClose = useCallback(() => {
    // Swallow the synthesized post-tap click that would otherwise close the card.
    if (performance.now() < openGuardUntilRef.current) return;
    // Re-centre the map on the marker so the geo point is at the viewport
    // centre once the card deflates (no card-framing offset needed anymore).
    const loc = tokenInfoRef.current?.meta?.location;
    if (mapRef.current && loc && Number.isFinite(loc.lon) && Number.isFinite(loc.lat)) {
      mapRef.current.flyTo({ center: [loc.lon, loc.lat], duration: 600, offset: [0, 0] });
    }
    setIsModalOpen(false);
    setDetailOpen(false);
    setFlyingToId(null);
    setRevealOrigin(null);
    setTokenInfo(null);
    setStripLands(null);
    clearAssetParam();
    // a11y: hand focus back to the marker the card bloomed from.
    openAnchorElRef.current?.querySelector<HTMLElement>(".ef-bloom")?.focus({ preventScroll: true });
  }, [clearAssetParam]);

  // Close the bloomed card with Escape (the inline detail has its own handler).
  useEffect(() => {
    if (!isModalOpen || detailOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleModalClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, detailOpen, handleModalClose]);

  // Marker bloom content lifecycle: mount the card content as soon as a land is
  // open; keep it mounted for one shell-deflate past close so it can fade out.
  const openMarkerId = isModalOpen && tokenInfo != null ? tokenInfo.id : null;
  useEffect(() => {
    if (openMarkerId != null) {
      setCardMountId(openMarkerId);
      return;
    }
    const timeout = setTimeout(() => setCardMountId(null), BLOOM_CLOSE_MS + 100);
    return () => clearTimeout(timeout);
  }, [openMarkerId]);

  // a11y: move focus into the bloomed card once its content mounts (also runs
  // when the inline detail collapses back to the card).
  useEffect(() => {
    if (isModalOpen && !detailOpen && cardMountId != null) {
      openCardContentRef.current?.focus({ preventScroll: true });
    }
  }, [isModalOpen, detailOpen, cardMountId]);

  // Desktop: clicking the popup reveals the full detail inline at the click point
  // (drives the hero slide-left + staggered section reveal).
  const handleOpenDetail = useCallback(
    (origin: { x: number; y: number }) => {
      setRevealOrigin(origin);
      setDetailOpen(true);
      // Reflect the open detail in the URL (`?asset=<id>`) so a refresh or a
      // shared/bookmarked link re-opens this land instead of dropping back to the
      // bare map. Mark it as already handled so the deep-link effect below doesn't
      // redundantly re-run its fly-to / re-open work for the asset we just opened.
      const id = tokenInfoRef.current?.id;
      if (id != null) {
        handledAssetRef.current = String(id);
        const next = new URLSearchParams(searchParams);
        next.set("asset", String(id));
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  // Closing the inline detail returns straight to the closed marker (both desktop
  // and mobile) — NOT back to the bloomed LandCardPopup. It's a full teardown:
  // re-centre the map on the pin, clear the selection, and hand focus back to the
  // marker (same path as dismissing the card), so exiting the LandPage embed never
  // flashes the card on the way out.
  const handleDetailClose = useCallback(() => {
    handleModalClose();
  }, [handleModalClose]);

  // Called when the embedded LandPage's feature tags are clicked — closes the
  // detail panel and runs the term through the map's own search box (which
  // narrows the map to the results). Deliberately not `?search=`: that param is
  // the header search, which opens the best match without filtering the map.
  // Via a ref because handleSearchType is declared further down.
  const runMapSearchRef = useRef<((term: string) => void) | null>(null);
  const handleEmbeddedSearch = useCallback(
    (term: string) => {
      handleDetailClose();
      setSearch(term);
      runMapSearchRef.current?.(term);
    },
    [handleDetailClose]
  );

  // Close the inline detail with Escape.
  useEffect(() => {
    if (!detailOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDetailClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailOpen, handleDetailClose]);

  // On mobile, slide the fixed header up and bottom nav down while the card popup
  // or inline land detail is open, so they don't cover the content.
  useEffect(() => {
    if (!isMobile) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dur = prefersReducedMotion ? "0s" : "0.38s";
    const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
    const headerWrap = document.getElementById("eco-explore-header-wrap");
    const bottomNav = document.getElementById("eco-bottom-navbar");
    const shouldHide = isModalOpen || detailOpen;

    if (headerWrap) {
      headerWrap.style.transition = `transform ${dur} ${ease}`;
      headerWrap.style.transform = shouldHide ? "translateY(-100%)" : "translateY(0)";
    }
    if (bottomNav) {
      bottomNav.style.transition = `transform ${dur} ${ease}`;
      bottomNav.style.transform = shouldHide ? BOTTOM_NAV_HIDDEN_TRANSFORM : "translateY(0)";
    }

    return () => {
      if (headerWrap) {
        headerWrap.style.transform = "";
        headerWrap.style.transition = "";
      }
      if (bottomNav) {
        bottomNav.style.transform = "";
        bottomNav.style.transition = "";
      }
    };
  }, [isMobile, isModalOpen, detailOpen]);

  // Deep-link: `/explore?asset=<id>` opens the full inline land detail (the
  // embedded LandPage) for that token. This is where every in-app "open the
  // LandPage" link now points; `?id=` still just opens the popup ("show on map").
  // Waits for the lands list, flies the map to the land, then reveals the detail.
  useEffect(() => {
    const assetParam = searchParams.get("asset");
    if (!assetParam) {
      handledAssetRef.current = null;
      return;
    }
    if (handledAssetRef.current === assetParam) return;
    if (!landsLoaded) return; // wait for the lands fetch — an empty map is still a loaded map
    handledAssetRef.current = assetParam;

    const id = Number(assetParam);
    const found = allData.find((item: any) => item.id === id);
    // If the land isn't in the map dataset, still open the detail by id — the
    // embedded LandPage fetches its own full data from the API by tokenId.
    setTokenInfo(found ?? { id });
    setRevealOrigin(null);

    const openDetailAfterFly = () => {
      afterFlyLands(() => {
        setIsModalOpen(true);
        setDetailOpen(true);
      });
    };

    const resolveLocation = async () => {
      if (found) {
        const lon = found.meta?.location?.lon;
        const lat = found.meta?.location?.lat;
        if (Number.isFinite(lon) && Number.isFinite(lat) && mapRef.current) {
          flyToLand(lon, lat, { duration: 2000, meta: found.meta });
          openDetailAfterFly();
        } else {
          setIsModalOpen(true);
          setDetailOpen(true);
        }
      } else {
        // Land isn't in the map dataset — fetch its location so we can fly the map
        // and place a marker for it (the markers memo renders tokenInfo if not in allData).
        // getFullData only works for minted lands; for un-minted lands (etNft === null)
        // it returns { error: "NFT not found for this token" }, so we fall back to the
        // owner-scoped user/eco-tokens endpoint — the same fallback LandPage uses.
        let record: any = null;
        try {
          const full = await axios.get(ApiRoutes.token.getFullData(String(id))).then((res) => res.data?.data);
          if (full?.token) {
            record = full.token;
          }
        } catch (e) {
          console.error("getFullData failed for non-map land", id, e);
        }
        if (!record) {
          try {
            const fb = await axios.get(ApiRoutes.user.getToken(String(id))).then((res) => res.data?.data);
            if (fb?.token) {
              record = fb.token;
            }
          } catch (e) {
            console.error("Could not fetch location for non-map land", id, e);
          }
        }
        if (record) {
          const rawLocation = record.meta?.location;
          const location = rawLocation
            ? typeof rawLocation === "string"
              ? JSON.parse(rawLocation)
              : rawLocation
            : null;
          if (location && Number.isFinite(location.lon) && Number.isFinite(location.lat)) {
            setTokenInfo({ ...record, meta: { ...record.meta, location } });
            if (mapRef.current) {
              flyToLand(location.lon, location.lat, { duration: 2000, meta: record.meta });
              openDetailAfterFly();
              return;
            }
          }
        }
        // No coords resolved — open detail immediately so the page isn't stuck.
        setIsModalOpen(true);
        setDetailOpen(true);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    resolveLocation();
    // flyToLand/afterFlyLands are stable (no deps change them), so omitting them is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allData, landsLoaded]);

  const handleMouseClick = useCallback((event: any) => {
    // If we're clicking a marker, don't process the map click
    if (event.originalEvent.target.closest(".mapboxgl-marker")) {
      return;
    }

    const { features } = event;
    const selected = features && features[0];

    if (selected) {
      const meta = JSON.parse(selected.properties.meta);
      const trust = JSON.parse(selected.properties.trust);
      const etNft = JSON.parse(selected.properties.etNft);
      const clickedToken = {
        ...selected.properties,
        meta: {
          ...meta,
          location: meta.location,
        },
        trust,
        etNft,
      };
      console.log({ clickedArea: clickedToken });
      if (mapRef && mapRef.current) {
        mapRef.current.flyTo({
          zoom: landZoom(meta, meta.location.lat),
          center: [meta.location.lon, meta.location.lat],
          duration: 3000,
        });
      }
      setTokenInfo(clickedToken);
      window.scrollTo({ top: infoRef.current.offsetParent.offsetTop, behavior: "smooth" });
    } else {
      // Swallow the synthesized post-tap click that would otherwise close the card.
      if (performance.now() < openGuardUntilRef.current) return;
      // Only clear tokenInfo if we're not clicking a marker
      setTokenInfo(null);
      setIsModalOpen(false);
    }
  }, []);

  const getLandSizeLimits = async () => {
    try {
      // Read via read-only RPC so map mount never prompts MetaMask
      const contract = getReadOnlyContract("tree-token");
      if (contract) {
        const max = await contract.maximumEasementLandSize();
        setSizeMax(Number(max));
      }
    } catch (e: any) {
      console.log({ e });
    }
  };
  const markers = useMemo(() => {
    const dataToRender = filteredData.length > 0 ? filteredData : allData;

    // If tokenInfo has location data but isn't already in the map dataset (e.g.
    // opened via ?asset= for a land the map endpoint doesn't return), append it as
    // a synthetic entry so it gets a visible pin on the map.
    let entriesToRender: any[] = dataToRender ? [...dataToRender] : [];
    if (tokenInfo?.meta?.location && !entriesToRender.some((item: any) => item.id === tokenInfo.id)) {
      entriesToRender = [...entriesToRender, tokenInfo];
    }

    if (entriesToRender.length > 0) {
      // Filter out expired lands before rendering markers
      const nonExpiredData = entriesToRender.filter((token: any) => {
        const contractStart = token.etNft?.easementActivated?.contractStart;
        const contractPeriod = token.meta?.contractPeriod;
        // If no contract start date, include the land (not yet activated)
        if (!contractStart) return true;
        // Check if contract has expired
        const isExpired = moment(contractStart).add(contractPeriod, TERM_UNIT).isBefore(moment());
        const offerOpen = token.offerStatus === "opened";
        const isActive = token.etNft?.easementActivated?.active === true;
        return !isExpired || offerOpen || isActive;
      });
      console.log({ dataToRender: nonExpiredData });
      // On a dense phone map, the per-marker infinite pulse rings are the jank
      // source — flip every marker to a static glow instead (see .ef-dense).
      const denseGlow = isMobile && nonExpiredData.length > MOBILE_DENSE_MARKER_LIMIT;
      return nonExpiredData.map((item: any, index: number) => {
        const location = item.meta.location;
        if (!location) return null;
        // Small static thumb keeps dense maps light; the bloomed card (LandCardPopup)
        // upgrades to the static poster, and only the detail view loads the animated one.
        const landImage = landImageSrc(item, "thumb");
        const sizeStr = item.meta.size ?? "0,ha";
        const [landSize, unit] = sizeStr.split(",");
        const landArea = convertToParcels(Number(landSize), unit);
        const status = item.etNft?.status;
        const publicStatus = resolveStandardStatus(
          { status: item.status, etNft: item.etNft, offerStatus: item.offerStatus, meta: item.meta },
          { role: "public" }
        );
        const isActiveStatus =
          publicStatus?.label === "Active" || publicStatus?.label === "Offer" || publicStatus?.label === "Auction";
        type SizeCategory = "small" | "medium" | "large" | "extraLarge";
        type SizeMatrix = {
          [K in SizeCategory]: {
            [L in SizeCategory]: number;
          };
        };

        // Define size matrix for [zoom level][land area category]
        const sizeMatrix: SizeMatrix = {
          small: {
            // zoom <= 5
            small: 32, // <= 1000 parcels
            medium: 48, // 1001-2000 parcels
            large: 64, // > 2000 parcels
            extraLarge: 80,
          },
          medium: {
            // 5 < zoom <= 10
            small: 32, // <= 1000 parcels
            medium: 48, // 1001-2000 parcels
            large: 64, // > 2000 parcels
            extraLarge: 80,
          },
          large: {
            // zoom > 10
            small: 48,
            medium: 64,
            large: 96,
            extraLarge: 128,
          },
          extraLarge: {
            // for extraLarge zoom (if needed)
            small: 48,
            medium: 64,
            large: 96,
            extraLarge: 128,
          },
        };

        // Determine land area category
        let areaCategory: SizeCategory;
        if (landArea <= sizeMax * 0.25) {
          areaCategory = "small";
        } else if (landArea <= sizeMax * 0.5) {
          areaCategory = "medium";
        } else if (landArea <= sizeMax * 0.75) {
          areaCategory = "large";
        } else {
          areaCategory = "extraLarge";
        }

        // Determine zoom category
        let zoomCategory: SizeCategory;
        if (currentZoom <= 5) {
          zoomCategory = "small";
        } else if (currentZoom <= 10) {
          zoomCategory = "medium";
        } else {
          zoomCategory = "large";
        }

        const markerSize = sizeMatrix[zoomCategory][areaCategory];

        const lat = location.lat > 90 ? 90 : location.lat < -90 ? -90 : location.lat;
        const lon = location.lon > 180 ? 180 : location.lon < -180 ? -180 : location.lon;

        // Marker → card bloom state. `isOpen` drives the morph; `showCard`
        // lingers one deflate past close so the content can fade out.
        // Hidden while the full inline detail is open (the detail covers it).
        const isOpen = isModalOpen && tokenInfo?.id === item.id && !detailOpen && flyingToId !== item.id;
        const showCard = cardMountId != null && cardMountId === item.id && !detailOpen && flyingToId !== item.id;
        // The open marker renders tokenInfo (it can be richer than the map row).
        const cardToken = tokenInfo?.id === item.id ? tokenInfo : item;
        const sale = isOpen || showCard ? getSaleDescription(cardToken) : null;
        const anchorClasses = [
          isOpen ? "ef-open" : "",
          showCard && openBelow ? "ef-below" : "",
          status === "minted" ? "ef-minted" : "",
          // Mobile dense field — static glow instead of the live pulse rings.
          denseGlow ? "ef-dense" : "",
          // Non-active public status: gray non-glowing marker.
          !isActiveStatus ? "ef-inactive" : "",
        ]
          .filter(Boolean)
          .join(" ");
        // CSS vars: closed marker size + how far the card floats above the
        // point (extra lift makes room for the sale bar under the card).
        const anchorVars: any = {
          "--msize": `${markerSize}px`,
          "--ef-lift": `${BLOOM_GAP + (sale ? BLOOM_SALE_BAR_H : 0)}px`,
        };

        return (
          <Marker
            key={item.id ?? index}
            // Mapbox owns this element's transform for positioning — never
            // animate it; the bloom only ever animates inner elements. The
            // z-index bump lifts the open marker above MapDismiss and siblings.
            // will-change only on the open marker (it rides the fly-to): a
            // permanent hint on every pin would keep one GPU layer alive per
            // land — death by a thousand layers once the map is dense.
            style={{
              left: 0,
              opacity: 1,
              position: "absolute",
              top: 0,
              willChange: isOpen ? "transform" : undefined,
              zIndex: isOpen ? 1100 : 1,
            }}
            latitude={lat}
            longitude={lon}
          >
            <PulseObservedAnchor
              observer={pulseObserver}
              callbacks={pulseCallbacksRef}
              className={anchorClasses}
              style={anchorVars}
            >
              <span className="ef-pulse" />
              <span className="ef-pulse" />
              <span className="ef-ripple" />
              <span className="ef-stem" />
              <span className="ef-dot" />
              <div
                className="ef-bloom"
                role="button"
                tabIndex={isOpen ? -1 : 0}
                aria-label={`Land marker ${item.meta?.name || ""}`}
                aria-expanded={isOpen}
                onClick={isOpen ? undefined : (e) => handleMarkerClick(item, e.currentTarget.parentElement)}
                onKeyDown={
                  isOpen
                    ? undefined
                    : (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleMarkerClick(item, e.currentTarget.parentElement);
                        }
                      }
                }
              >
                {/* Same URL as before the restructure — browser cache, no refetch */}
                <span className="ef-photo" style={{ backgroundImage: `url(${landImage})` }} />
              </div>
              {showCard && (
                <div className="ef-cc" ref={openCardContentRef} tabIndex={-1}>
                  <LandCardPopup
                    token={cardToken}
                    open
                    inline
                    onClose={handleModalClose}
                    onOpenDetail={handleOpenDetail}
                  />
                  {/* <button className="ef-close" aria-label="Close" onClick={handleModalClose}>
                    ✕
                  </button> */}
                </div>
              )}
            </PulseObservedAnchor>
          </Marker>
        );
      });
    } else {
      return null;
    }
    // handleMarkerClick/handleModalClose/handleOpenDetail are stable in behavior; omitting them is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredData,
    allData,
    currentZoom,
    tokenInfo,
    isModalOpen,
    cardMountId,
    openBelow,
    detailOpen,
    flyingToId,
    isMobile,
  ]);

  // `term` defaults to the box's value; handleEmbeddedSearch passes its own
  // because the setSearch it just queued hasn't rendered yet.
  const handleSearchType = async (e: any, term: string = search) => {
    setTokenInfo(null);
    if (e.key === "Enter") {
      setLoading(true);
      try {
        const result = await axios.get(ApiRoutes.ecoBank.lands.search(term)).then((res) => res.data);
        const mapData = await axios.get(ApiRoutes.map.root).then((res) => res.data);
        const foundLands = [] as any[];
        result.forEach((foundLand: any) => {
          const similarLand = mapData.find((land: any) => land.id === foundLand.id);
          if (similarLand) {
            foundLands.push(similarLand);
          }
        });
        const parsedResult = foundLands.map(parseMapLand);
        setAllData(parsedResult);
        if (foundLands.length === 1) {
          const similarLand = parsedResult[0];
          setOpenBelow(false);
          setTokenInfo(similarLand);
          setIsModalOpen(true);
          console.log({ similarLand });
          if (mapRef && mapRef.current) {
            const lon = similarLand.meta.location.lon;
            const lat = similarLand.meta.location.lat;
            flyToLand(lon, lat, { meta: similarLand.meta });
          }
        } else if (foundLands.length > 1) {
          setTokenInfo(null);
          setLocationDetails(null);
          // Cleared panel: let the next settle geocode even if the centre is unchanged.
          geocodedKeyRef.current = null;
          mapRef.current?.flyTo({ zoom: 7, center: [80.9425, 7.1924], duration: 3000 });
        } else {
          setTokenInfo(null);
          setLocationDetails(null);
          geocodedKeyRef.current = null;
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setSearch(e.target.value);
    }
  };
  runMapSearchRef.current = (term: string) => handleSearchType({ key: "Enter" }, term);

  // Set by the header-search effect when it empties the box itself (it restores
  // the full lands list and opens a card), so the reset below doesn't refetch
  // and fly the camera away from that card.
  const skipSearchResetRef = useRef(false);
  const didMountSearchEffect = useRef(false);
  useEffect(() => {
    if (!didMountSearchEffect.current) {
      didMountSearchEffect.current = true;
      return;
    }
    if (skipSearchResetRef.current) {
      skipSearchResetRef.current = false;
      return;
    }
    if (search === "") {
      fetchLands();
      setTokenInfo(null);
      mapRef.current?.flyTo({ zoom: 7, center: [80.9425, 7.1924], duration: 3000 });
    }
  }, [search]);

  // The initial location panel comes from the first settle (handleMapMove /
  // handleMapLoad), not from a second request here.
  useEffect(() => {
    fetchLands();
  }, []);

  const clearSearch = () => {
    setSearch("");
    setTokenInfo(null);
    setLocationDetails(null);
    geocodedKeyRef.current = null;
  };

  useEffect(() => {
    console.log({ tokenInfo });
  }, [tokenInfo]);

  // flagcdn keys flags by ISO 3166-1 alpha-2, so the URL is derivable from the country
  // code we already have — no API round trip. The result is only ever an <img src>, which
  // is not subject to CORS. (restcountries v3.1 was decommissioned: every path now 301s to
  // a deprecation notice, and the redirect itself carries no Access-Control-Allow-Origin,
  // so the browser blocked it before the body could even be read.)
  const countryFlagUrl = (countryCode: string) => `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;

  // Callers go through geocodeSettledCenter, which gates on panel visibility.
  const fetchLocationDetails = async (lat: number, lon: number) => {
    const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
    if (key === geocodedKeyRef.current) {
      return;
    }
    geocodedKeyRef.current = key;
    // A newer centre supersedes the one in flight, so a slow response can't overwrite it.
    geocodeAbortRef.current?.abort();
    const controller = new AbortController();
    geocodeAbortRef.current = controller;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${MAPBOX_TOKEN}`,
        { signal: controller.signal }
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const countryFeature = data.features.find((f: any) => f.place_type.includes("country"));
        const countryCode = countryFeature?.properties?.short_code?.toUpperCase() || "";

        const details = {
          city: data.features.find((f: any) => f.place_type.includes("place"))?.text || "",
          country: countryFeature?.text || "",
          countryCode,
          longitude: lon.toFixed(6),
          latitude: lat.toFixed(6),
        };
        setLocationDetails(details);

        setCountryFlag(countryCode ? countryFlagUrl(countryCode) : null);
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      // Let the same centre be retried on the next settle.
      if (geocodedKeyRef.current === key) {
        geocodedKeyRef.current = null;
      }
      console.error("Error fetching location details:", error);
    }
  };

  // Update location details when map loads (a no-op if the first settle already
  // geocoded this centre).
  const handleMapLoad = () => {
    geocodeSettledCenter();
  };

  const selectLand = useCallback(
    (item: any) => {
      // Thumbnail-strip selection: the fly-to frames the land for an upward
      // bloom, so the previous marker's "below" clamp never carries over.
      setOpenBelow(false);
      setTokenInfo(item);
      const lon = item.meta?.location?.lon;
      const lat = item.meta?.location?.lat;
      if (mapRef.current && lon != null && lat != null) {
        // Fly to the land first, then bloom: suppress this id's popup until the
        // camera arrives. The functional updater ignores a stale arrival from a
        // fly that a newer strip selection has already superseded.
        setFlyingToId(item.id);
        const reveal = () => setFlyingToId((cur: any) => (cur === item.id ? null : cur));
        flyToLand(lon, lat, { meta: item.meta });
        afterFlyLands(reveal);
        // Fallback in case moveend never fires (e.g. the camera doesn't move).
        setTimeout(reveal, 3300);
      } else {
        setFlyingToId(null);
      }
    },
    // flyToLand is stable (no deps change it), so omitting it is safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const navigateLand = useCallback(
    (direction: "left" | "right") => {
      if (!stripData.length || !tokenInfoRef.current) return;
      const currentIndex = stripData.findIndex((item: any) => item.id === tokenInfoRef.current.id);
      if (currentIndex === -1) return;
      const nextIndex =
        direction === "left"
          ? (currentIndex - 1 + stripData.length) % stripData.length
          : (currentIndex + 1) % stripData.length;
      selectLand(stripData[nextIndex]);
    },
    [stripData, selectLand]
  );

  // Auto-scroll thumbnail strip to keep selected item centred
  useEffect(() => {
    if (!isModalOpen || !tokenInfo || !thumbnailStripRef.current) return;
    const index = stripData.findIndex((item: any) => item.id === tokenInfo.id);
    if (index === -1) return;
    const ITEM_WIDTH = 68; // 58px + 10px gap
    const containerWidth = thumbnailStripRef.current.clientWidth;
    const scrollPos = index * ITEM_WIDTH - containerWidth / 2 + ITEM_WIDTH / 2;
    thumbnailStripRef.current.scrollTo({ left: Math.max(0, scrollPos), behavior: "smooth" });
  }, [tokenInfo, isModalOpen, stripData]);

  // Header search → the popup view: the best match's bloomed card plus a
  // thumbnail strip of only the matches (stripLands). Two params, both one-shot
  // commands consumed (replace-removed) once handled, so repeating a search —
  // even while already on Explore — always changes the URL and re-runs:
  //   ?id=<landId>    open that land (header exact-ID hit, HomeHero CTA). Only
  //                   the header's hit (state.headerSearch) narrows the strip;
  //                   the HomeHero CTA keeps every land to browse.
  //   ?search=<term>  open the best-ranked match from the lands index
  // Unlike the map's own search box (handleSearchType) the markers are never
  // narrowed — closing the card returns to the full map.
  const allDataRef = useRef(allData);
  allDataRef.current = allData;
  const searchBoxRef = useRef(search);
  searchBoxRef.current = search;
  useEffect(() => {
    const idParam = searchParams.get("id");
    const term = searchParams.get("search")?.trim();
    if ((!idParam && !term) || !landsLoaded) {
      return;
    }
    const fromHeaderSearch = Boolean(term) || (routerLocation.state as { headerSearch?: boolean } | null)?.headerSearch;
    // Any URL change (a newer search) or unmount drops this run — a late
    // setSearchParams would otherwise navigate back to Explore.
    let cancelled = false;

    const resolve = async () => {
      // The search box narrows allData to its results — restore every land.
      const lands: any[] = searchBoxRef.current
        ? (await axios.get(ApiRoutes.map.root).then((res) => res.data)).map(parseMapLand)
        : allDataRef.current;
      if (!term) {
        const match = lands.find((land) => land.id === Number(idParam));
        return { lands, matches: match ? [match] : [] };
      }
      const hits: any[] = await axios.get(ApiRoutes.ecoBank.lands.search(term)).then((res) => res.data);
      // Hits come back best-first; keep the ones the map can show, in that order.
      const matches = hits
        .map((hit) => lands.find((land) => land.id === hit.id))
        .filter((land): land is any => land !== undefined);
      return { lands, matches };
    };

    resolve()
      .catch((e) => {
        console.error("Header search failed", e);
        return { lands: allDataRef.current, matches: [] as any[] };
      })
      .then(({ lands, matches }) => {
        const match = matches[0];
        if (cancelled) {
          return;
        }
        if (lands !== allDataRef.current) {
          skipSearchResetRef.current = true;
          setSearch("");
          setAllData(lands);
        }
        setLandViewMode("all");
        setFilteredData([]);
        if (match) {
          setDetailOpen(false);
          setRevealOrigin(null);
          setStripLands(fromHeaderSearch ? matches : null);
          handledAssetRef.current = null;
          const loc = match.meta?.location;
          if (idParam && cameFromLandPage && Number.isFinite(loc?.lon) && Number.isFinite(loc?.lat)) {
            // From the LandPage's "View on map": the map already mounted on the
            // land (initialViewState), so no fly-to — the jump only applies the
            // card-framing offset.
            setOpenBelow(false);
            setFlyingToId(null);
            setTokenInfo(match);
            flyToLand(loc.lon, loc.lat, { jump: true, meta: match.meta });
          } else {
            // Same as picking it from the strip: fly there, bloom on arrival.
            selectLand(match);
          }
          setIsModalOpen(true);
        }
        const next = new URLSearchParams(searchParams);
        next.delete("id");
        next.delete("search");
        if (match) {
          next.delete("asset");
        }
        setSearchParams(next, { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // flyToLand/selectLand are stable; lands are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, landsLoaded]);

  // Lock the map while a card or the inline detail is open: the user can't zoom,
  // pan, rotate or fling to another location until BOTH are dismissed. Only user
  // input gestures are frozen — programmatic camera moves (the marker bloom
  // fly-to, thumbnail-strip navigation, deep-link fly) still run, since those go
  // through the map API rather than the input handlers (see the handler props on
  // <Map>). react-map-gl reconciles these enable/disable on every prop change.
  const mapInteractionsLocked = isModalOpen || detailOpen;
  return (
    <>
      <MapContainer
        sx={{
          width: "100vw",
          height: "100vh",
          backgroundColor: "back.green",
          borderRadius: "0px",
          // Below md the floating bottom nav and the search box sit over the map's bottom corners,
          // which would hide the Mapbox logo and attribution (they must stay visible). Lift both
          // corners to just above the search box; with a card or the detail open, the nav and
          // search are gone and the corners drop back to the edge.
          ...(isTablet &&
            !(isModalOpen || detailOpen) && {
              "& .mapboxgl-ctrl-bottom-left, & .mapboxgl-ctrl-bottom-right": {
                bottom: `${searchBottom + SEARCH_HEIGHT + MAP_CTRL_GAP}px`,
              },
            }),
        }}
      >
        <Map
          ref={mapRef as any}
          projection={{ name: "globe" }}
          maxZoom={LAND_MAX_ZOOM}
          // Map lock (see mapInteractionsLocked): freeze every user navigation
          // gesture while a card / the detail is open; programmatic moves still work.
          scrollZoom={!mapInteractionsLocked}
          boxZoom={!mapInteractionsLocked}     
          dragRotate={!mapInteractionsLocked}
          dragPan={!mapInteractionsLocked}
          keyboard={!mapInteractionsLocked}
          doubleClickZoom={!mapInteractionsLocked}
          touchZoomRotate={!mapInteractionsLocked}
          touchPitch={!mapInteractionsLocked}
          style={{
            overflowX: "hidden",
            width: "100vw",
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 0,
            borderRadius: "0px",
          }}
          id="map-box-container"
          onZoom={handleZoomChange}
          onMoveEnd={handleMapMove}
          onLoad={handleMapLoad}
          interactiveLayerIds={["map-data-fill"]}
          initialViewState={initialViewState}
          mapStyle={MAPBOX_STYLE}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapLib={loadMapboxgl()}
          onClick={handleMouseClick}
        >
          {locationDetails && !(isModalOpen || detailOpen) && (
            <LocationDetailsContainer style={{ top: `${headerHeight + (isMobile ? 24 : 40)}px` }}>
              <LocationDetail>
                {countryFlag && (
                  <img
                    src={countryFlag}
                    alt={`${locationDetails.country} flag`}
                    style={{
                      width: "28px",
                      height: "20px",
                      marginRight: "10px",
                      objectFit: "contain",
                      borderRadius: "2px",
                    }}
                  />
                )}
              </LocationDetail>
              {locationDetails.city && (
                <LocationDetail sx={{ fontSize: "26px" }}>{locationDetails.city}</LocationDetail>
              )}

              <LocationDetail sx={{ fontSize: "26px", display: "flex", alignItems: "center" }}>
                {locationDetails.country}
              </LocationDetail>
              <LocationDetail sx={{ fontSize: "16px", fontFamily: "monospace" }}>
                {locationDetails.longitude}°, {locationDetails.latitude}°
              </LocationDetail>
            </LocationDetailsContainer>
          )}{" "}
          {/* Land view toggle buttons in the top right — hidden while the popup or
              inline detail is open so they don't overlap the detail view. */}
          {!(isModalOpen || detailOpen) && (
            <MapButtonsContainer style={{ top: `${headerHeight + (isMobile ? 8 : 20)}px` }}>
              <MapButton onClick={() => toggleLandViewMode("active")} active={landViewMode === "active"}>
                <PulseCircle sold={true} sx={{ mr: 0 }} />
                ACTIVE
              </MapButton>
              <MapButton onClick={() => toggleLandViewMode("live")} active={landViewMode === "live"}>
                <ListAltOutlined sx={{ fontSize: 14 }} />
                LISTED
              </MapButton>
            </MapButtonsContainer>
          )}
          {/* Add GeolocateControl for user location */}
          <GeolocateControl
            position="bottom-right"
            positionOptions={{ enableHighAccuracy: true }}
            trackUserLocation={true}
            showUserHeading={true}
            showAccuracyCircle={true}
            style={{
              zIndex: 1000,
              marginBottom: isMobile ? "90px" : isTablet ? "100px" : "120px",
              marginRight: isMobile ? "5px" : "20px",
            }}
          />
          <ScaleControl
            position="bottom-left"
            style={{
              marginBottom: isMobile ? "90px" : isTablet ? "100px" : "120px",
              marginLeft: isMobile ? "5px" : "20px",
              zIndex: 1000,
            }}
          />
          {markers}
          {/* Popup-phase dismiss layer (transparent — the map stays fully
              visible behind the bloomed card). It lives INSIDE the map
              container so it can sit underneath the open marker (z 1100) while
              still catching clicks on the canvas, the other markers and the
              map controls (z ≤ 1000) — a fixed full-screen backdrop (z 1300)
              would also cover the marker-attached card. Clicking it dismisses
              the card. */}
          {tokenInfo && !detailOpen && <MapDismiss onClick={handleModalClose} />}
        </Map>

        {/* Hidden while the LandCardPopup or the inline land detail is open, so it
            doesn't float over the land-viewing flow (matches the location details
            and map-toggle buttons). */}
        {!(isModalOpen || detailOpen) && (
          <SearchContainer style={{ bottom: `${searchBottom}px` }}>
            <SearchInput
              inputMode="search"
              size="medium"
              id="search"
              sx={{
                width: { xs: "100%" },
                backgroundColor: "#2b3501",
                color: "white",
              }}
              inputProps={{
                enterKeyHint: "search",
              }}
              disabled={isLoading}
              placeholder="Type flora, fauna, land feature or property name…"
              value={search}
              onKeyUp={handleSearchType}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <ClearIcon onClick={clearSearch} />}
          </SearchContainer>
        )}
      </MapContainer>

      {/* Transparent backdrop for the inline-detail phase only. While the popup
          card is bloomed on the marker, click-to-dismiss comes from MapDismiss
          inside the map container instead (so the card can sit above it); this
          fixed layer mounts in the same commit MapDismiss unmounts, so there is
          never a gap where a stray click reaches the map. Clicking it dismisses
          the detail. The map is deliberately NOT dimmed in either phase. */}
      {tokenInfo && detailOpen && <PopupBackdrop onClick={handleDetailClose} />}

      {/* The popup card itself now renders inside the open marker (see the
          markers memo) and blooms out of it, so there is no centered overlay
          popup anymore — tapping the bloomed card reveals the detail inline. */}

      {/* Mobile-only back button rendered above the detail overlay. */}
      {isMobile && tokenInfo && detailOpen && (
        <MobileBackButton aria-label="Close land detail" onClick={handleDetailClose} style={{ top: 18 }}>
          <ArrowBackIosNew sx={{ fontSize: 18 }} />
        </MobileBackButton>
      )}

      {/* Inline full land detail rendered over the still-live map, opened by tapping
          the popup (desktop and mobile alike). Dismissed by clicking the surrounding
          map/backdrop — no explicit close button. */}
      {tokenInfo && detailOpen && (
        <LandPage
          key={tokenInfo.id}
          embedded
          tokenId={String(tokenInfo.id)}
          // Lightweight map token — lets the detail render its hero instantly
          // (and morph from the popup) while the full payload is still loading.
          previewToken={tokenInfo}
          revealOrigin={isMobile ? null : revealOrigin}
          // On mobile the header is hidden while the detail is open, so the
          // overlay starts at the very top; on desktop it sits below the header.
          topOffset={isMobile ? 0 : headerHeight}
          onClose={handleDetailClose}
          onSearch={handleEmbeddedSearch}
        />
      )}

      {/* Shown alongside the popup for browsing lands; hidden once the focused,
          lightbox-style detail is open (both desktop and mobile). */}
      {isModalOpen && !detailOpen && (
        <ThumbnailStrip style={{ top: isMobile ? "0px" : `${headerHeight}px` }}>
          {/* A single-land strip (e.g. an exact-ID search) has nowhere to step to;
              hidden rather than removed so the thumbnail stays centred. */}
          <ThumbnailArrow
            onClick={() => navigateLand("left")}
            style={{ visibility: stripData.length > 1 ? "visible" : "hidden" }}
          >
            <ChevronLeft />
          </ThumbnailArrow>
          <ThumbnailScroll ref={thumbnailStripRef}>
            {stripData.map((item: any) => (
              <ThumbnailItem key={item.id} active={tokenInfo?.id === item.id} onClick={() => selectLand(item)}>
                <img src={landImageSrc(item, "thumb")} alt={item.meta?.name || ""} loading="lazy" decoding="async" />
              </ThumbnailItem>
            ))}
          </ThumbnailScroll>
          <ThumbnailArrow
            onClick={() => navigateLand("right")}
            style={{ visibility: stripData.length > 1 ? "visible" : "hidden" }}
          >
            <ChevronRight />
          </ThumbnailArrow>
        </ThumbnailStrip>
      )}
    </>
  );
};

// Height of the medium outlined search field, used to lift the Mapbox corners above it.
const SEARCH_HEIGHT = 56;
const MAP_CTRL_GAP = 6;

const MapContainer = styled(Row)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;

  @media (max-width: 992px) {
    display: flex;
    flex-direction: column-reverse;
    & > div {
      margin: 0 auto;
      &#map-box-container {
        height: 100vh !important;
        width: 100vw !important;
      }
    }
  }
`;

const SearchContainer = styled("div")(() => ({
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  width: "80%",
  maxWidth: "650px",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  borderRadius: "20px",
  overflow: "hidden",
  border: "none",
  "@media (max-width: 992px)": {
    width: "90%",
  },
  "@media (max-width: 480px)": {
    width: "95%",
  },
}));

const ClearIcon = styled(Clear)(() => ({
  position: "absolute",
  zIndex: 20,
  cursor: "pointer",
  right: "1%",
  top: "30%",
  "&:hover": {
    color: "green",
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  border: "none",
  "& input": {
    zIndex: 1,
    border: "none",
    color: "white",
  },
  "& fieldset": {
    backgroundColor: theme.palette.back.explorerGreen,
    color: "white",
    border: "none",
  },
}));

const LocationDetailsContainer = styled("div")(() => ({
  position: "absolute",
  top: "160px", // Increased for potentially larger mobile header

  // Align with header content left edge: 24px gutter until max-width is reached,
  // then follow the centered 1560px container edge.
  left: "max(24px, calc((100vw - 1560px) / 2 + 24px))",
  width: "fit-content",
  maxWidth: "1560px", // Match header's maxWidth
  fontFamily: "Inter",
  backgroundColor: "transparent",
  zIndex: 1000,
  "@media (max-width: 992px)": {
    //top: "120px", // Adjust for mobile header
  },
  "@media (max-width: 540px)": {
    top: "130px",
    left: "24px",
  },
}));

const MapButtonsContainer = styled("div")(() => ({
  position: "absolute",
  top: "140px", // Adjusted to be below header
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  maxWidth: "1560px", // Match header's maxWidth
  backgroundColor: "transparent",
  zIndex: 1000, // Higher than map
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end", // Align buttons to the right side
  marginTop: "20px",
  paddingRight: "24px", // Account for header's paddingX + some extra space
  gap: "10px",
  "@media (max-width: 992px)": {
    // top: "120px", // Adjust for mobile header
    paddingRight: "24px", // Reduced padding for mobile
  },
  "@media (max-width: 540px)": {
    top: "130px", // Extra adjustment for very small screens
    paddingRight: "24px", // Even smaller padding for very small screens
    gap: "5px",
  },
}));

interface MapButtonProps {
  active: boolean;
}

const MapButton = styled("button")<MapButtonProps>(({ active }) => ({
  backgroundColor: active ? "#CCC" : "rgba(0, 0, 0, 0.45)",
  backdropFilter: active ? "none" : "blur(8px)",
  color: active ? "black" : "white",
  border: active ? "none" : "none",
  padding: "8px 11px",
  borderRadius: "12px",
  cursor: "pointer",
  fontFamily: "monospace",
  fontWeight: 700,
  fontSize: "14px",
  textTransform: "uppercase",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "6px",
  whiteSpace: "nowrap",
}));

const LocationDetail = styled("div")({
  marginBottom: "20px",
  fontFamily: "Inter",
  fontSize: "14px",
  color: "white",
  "&:last-child": {
    marginBottom: 0,
  },
});

const ThumbnailStrip = styled("div")(() => ({
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  maxWidth: "1560px",
  height: "88px",
  backgroundColor: "rgba(0, 0, 0, 0.72)",
  backdropFilter: "blur(12px)",
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 4px",
  borderRadius: "20px",
}));

const ThumbnailScroll = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "safe center",
  gap: "10px",
  overflowX: "auto",
  flex: 1,
  padding: "0 4px",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

interface ThumbnailItemProps {
  active: boolean;
}

const ThumbnailItem = styled("div")<ThumbnailItemProps>(({ active }) => ({
  flexShrink: 0,
  width: "58px",
  height: "58px",
  borderRadius: "12px",
  overflow: "hidden",
  cursor: "pointer",
  border: active ? "3px solid white" : "3px solid transparent",
  opacity: active ? 1 : 0.55,
  transition: "border-color 0.15s, opacity 0.15s",
  "&:hover": {
    opacity: 1,
    border: "3px solid rgba(255,255,255,0.5)",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

const ThumbnailArrow = styled("button")(() => ({
  flexShrink: 0,
  background: "none",
  border: "none",
  color: "white",
  cursor: "pointer",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
}));

// Invisible click-catcher under the inline detail: swallows map interaction and
// dismisses the detail on click. No tint — the live map stays fully visible.
const PopupBackdrop = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  cursor: "default",
  backgroundColor: "transparent",
}));

// Invisible click-catcher beneath a bloomed marker card. Rendered as a child of
// the <Map> (i.e. inside the map container's stacking context) so it covers the
// canvas, the other markers and the map controls (z ≤ 1000) while the open
// marker (z 1100) stays above it — see the markers memo. No tint.
const MapDismiss = styled("div")(() => ({
  position: "absolute",
  inset: 0,
  zIndex: 1050,
  backgroundColor: "transparent",
  cursor: "default",
}));

const MobileBackButton = styled("button")(() => ({
  position: "fixed",
  top: 16,
  left: 16,
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "none",
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  backdropFilter: "blur(8px)",
  color: "#fff",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  "&:active": { backgroundColor: "rgba(0, 0, 0, 0.65)" },
}));

// Root of a land marker: a 0×0 box pinned to the geo point. Mapbox positions
// the outer .mapboxgl-marker element (its transform is never animated here —
// that's what keeps the bloom glued to the map while panning/zooming); all
// motion happens on the children:
//   .ef-pulse  ×2 — idle expanding rings, paused/hidden while open
//   .ef-ripple    — one-shot leaf ripple on click (.ef-go armed imperatively)
//   .ef-dot/.ef-stem — glowing anchor left at the point while the card floats
//   .ef-bloom     — THE morphing shell: photo circle ⇄ 300×321 card frame
//   .ef-cc        — the real LandCardPopup overlaid at the open-card position
// Modifier classes: .ef-open (card showing), .ef-below (card opens downward
// when there's no room above), .ef-minted (white idle border).
const MarkerBloomAnchor = styled("div")(() => ({
  position: "relative",
  width: 0,
  height: 0,
  // contain: layout size limits the scope of the width/height transitions on
  // .ef-bloom so recalcs don't escape to sibling markers or the map canvas.
  contain: "layout size",

  // Idle pulse rings — transform/opacity only, so they stay on the compositor.
  "& .ef-pulse": {
    position: "absolute",
    left: 0,
    top: 0,
    width: "var(--msize)",
    height: "var(--msize)",
    borderRadius: "50%",
    border: "2px solid #8CC63F",
    opacity: 0,
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
    animation: `${bloomPulse} 2400ms ease-out infinite`,
    "&:nth-of-type(2)": { animationDelay: "1200ms" },
  },
  "&.ef-open .ef-pulse": { animationPlayState: "paused", opacity: 0 },
  // .ef-offscreen = marker panned out of the viewport (shared IntersectionObserver
  // via PulseObservedAnchor). Pausing stops a dense map from compositing dozens
  // of off-screen ring layers every frame.
  "&.ef-offscreen .ef-pulse": { animationPlayState: "paused" },

  // .ef-dense = mobile with many markers (see MOBILE_DENSE_MARKER_LIMIT). The
  // two infinite ring layers per marker are what jank a dense phone map, so kill
  // the animation outright and stand in a static green halo on the shell — zero
  // per-frame compositing, so smoothness no longer scales with marker count.
  // Defined ahead of the .ef-open / :hover bloom rules below (equal specificity)
  // so those still win their own box-shadow when this is the open marker.
  "&.ef-dense .ef-pulse": { animation: "none" },
  "&.ef-dense .ef-bloom": {
    boxShadow:
      "0 0 0 2px rgba(140, 198, 63, 0.35), 0 0 16px 3px rgba(140, 198, 63, 0.3), 0 6px 18px rgba(0, 0, 0, 0.5)",
  },

  // .ef-inactive = public status is not Active / Offer / Auction.
  // Kill the green pulse rings and replace the green border + glow with a
  // muted gray so inactive lands read as visually dormant on the map.
  "&.ef-inactive .ef-pulse": { animation: "none", opacity: 0 },
  "&.ef-inactive .ef-bloom": {
    borderColor: "#666",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.45)",
  },
  // Dense inactive: override the green static glow with the gray one.
  "&.ef-inactive.ef-dense .ef-bloom": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.45)",
  },

  // One-shot click ripple (.ef-go is toggled from the click handler).
  "& .ef-ripple": {
    position: "absolute",
    left: 0,
    top: 0,
    width: "var(--msize)",
    height: "var(--msize)",
    borderRadius: "50%",
    border: "2px solid #ccff99",
    opacity: 0,
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
  },
  "& .ef-ripple.ef-go": { animation: `${bloomRipple} 600ms ${BLOOM_EASE} both` },

  // Anchor dot + stem left behind at the geo point — delayed so they appear
  // just as the card lifts away from the marker.
  "& .ef-dot": {
    position: "absolute",
    left: 0,
    top: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#ccff99",
    boxShadow: "0 0 12px 3px rgba(204, 255, 153, 0.5)",
    pointerEvents: "none",
    transform: "translate(-50%, -50%) scale(0)",
    // Undelayed on close (dismissal never waits); the open rule adds the delay.
    transition: `transform 150ms ${BLOOM_EASE}`,
  },
  "&.ef-open .ef-dot": {
    transform: "translate(-50%, -50%) scale(1)",
    transition: `transform 350ms ${BLOOM_EASE} 250ms`,
  },
  // Grows by scaleY from its point-side end (compositor-only) rather than
  // animating height.
  "& .ef-stem": {
    position: "absolute",
    left: 0,
    bottom: 7,
    width: 1.5,
    height: 9,
    background: "linear-gradient(to top, #ccff99, rgba(204, 255, 153, 0.1))",
    pointerEvents: "none",
    transform: "translateX(-50%) scaleY(0)",
    transformOrigin: "bottom",
    transition: `transform 150ms ${BLOOM_EASE}`,
  },
  "&.ef-open .ef-stem": {
    transform: "translateX(-50%) scaleY(1)",
    transition: `transform 350ms ${BLOOM_EASE} 300ms`,
  },
  "&.ef-below .ef-stem": {
    bottom: "auto",
    top: 7,
    transformOrigin: "top",
    background: "linear-gradient(to bottom, #ccff99, rgba(204, 255, 153, 0.1))",
  },

  // THE morphing shell: photo circle ⇄ card frame. Geometry (width/height/
  // border-radius) must transition here — transform alone can't morph a circle
  // into a rounded rect while keeping a continuous cover photo. It's a single
  // element, click-driven one-shot, so the layout cost is contained; will-change
  // is hinted on hover/open only, never left on every idle marker.
  "& .ef-bloom": {
    position: "absolute",
    left: 0,
    top: 0,
    width: "var(--msize)",
    height: "var(--msize)",
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    border: "3px solid #8CC63F",
    overflow: "hidden",
    cursor: "pointer",
    backgroundColor: "#1a2a12",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.5)",
    // width/height/border-radius are layout/paint but intentional: transform
    // alone can't morph a circle into a rounded rect with a continuous photo.
    // box-shadow, border-color, border-width removed from transition — paint
    // per frame for no perceptible gain; they snap instantly instead.
    // This base rule is the close (deflate); .ef-open below carries the open.
    transition: bloomShellTransition(BLOOM_CLOSE_MS),
    "&:hover": {
      boxShadow: "0 0 0 4px rgba(204, 255, 153, 0.25), 0 8px 22px rgba(0, 0, 0, 0.55)",
      willChange: "transform",
    },
    "&:focus-visible": { outline: "2px solid #ccff99", outlineOffset: 2 },
  },
  "&.ef-minted .ef-bloom": { borderColor: "rgb(255, 255, 255)" },
  "&.ef-open .ef-bloom": {
    width: BLOOM_CARD_W,
    height: BLOOM_CARD_H,
    // The card floats above the point; --ef-lift adds room for the sale bar.
    transform: "translate(-50%, calc(-100% - var(--ef-lift, 14px)))",
    borderRadius: "20px", // matches LandCardPopup's CardBox
    borderWidth: 1,
    borderColor: "rgba(204, 255, 153, 0.35)",
    boxShadow: "0 26px 60px rgba(0, 0, 0, 0.6)",
    cursor: "default",
    // The real card sits on top — stray clicks fall through to MapDismiss (close).
    pointerEvents: "none",
    transition: bloomShellTransition(BLOOM_MS),
    // willChange is set imperatively in handleMarkerClick for the bloom duration
    // only — a permanent hint here would keep a large GPU layer alive forever.
  },
  "&.ef-open.ef-below .ef-bloom": { transform: `translate(-50%, ${BLOOM_GAP}px)` },

  // The land photo (same URL as the closed marker had — browser cache, no
  // refetch). Slightly zoomed while a dot, relaxing to 1 as it blooms.
  "& .ef-photo": {
    position: "absolute",
    inset: 0,
    backgroundSize: "cover",
    backgroundPosition: "center",
    transform: "scale(1.15)",
    transition: `transform ${BLOOM_CLOSE_MS}ms ${BLOOM_EASE}`,
  },
  "&.ef-open .ef-photo": { transform: "scale(1)", transition: `transform ${BLOOM_MS}ms ${BLOOM_EASE}` },

  // Real card content (LandCardPopup, untouched markup) overlaid at the
  // open-card position. Bottom-anchored so the optional sale bar simply grows
  // the stack upward; children are fully visible by default — the container
  // fade is what hides/reveals them, so the close fade takes everything along.
  "& .ef-cc": {
    position: "absolute",
    left: -BLOOM_CARD_W / 2,
    bottom: BLOOM_GAP,
    width: BLOOM_CARD_W,
    opacity: 0,
    pointerEvents: "none",
    outline: "none",
    transition: "opacity 150ms ease", // close: content clears early in the shell deflate
  },
  "&.ef-below .ef-cc": { bottom: "auto", top: BLOOM_GAP },
  "&.ef-open .ef-cc": {
    opacity: 1,
    pointerEvents: "auto",
    // Entrance is an animation so it still plays when the content mounts with
    // .ef-open already applied (the card only mounts for the open marker).
    animation: `${bloomContentFade} 300ms ease 260ms both`,
  },

  // Staggered content reveal, ~80ms apart from ~280ms into the morph:
  // chip → name/location → stats → sale bar. The ef-cc-* classes are no-op
  // hooks on LandCardPopup's own markup.
  "&.ef-open .ef-cc .ef-cc-chip": { animation: `${bloomContentIn} 450ms ${BLOOM_EASE} 280ms both` },
  "&.ef-open .ef-cc .ef-cc-title": { animation: `${bloomContentIn} 450ms ${BLOOM_EASE} 360ms both` },
  "&.ef-open .ef-cc .ef-cc-stats": { animation: `${bloomContentIn} 450ms ${BLOOM_EASE} 440ms both` },
  "& .ef-cc .ef-cc-sale": { position: "relative", overflow: "hidden" },
  "&.ef-open .ef-cc .ef-cc-sale": {
    animation: `${bloomContentIn} 450ms ${BLOOM_EASE} 520ms both`,
    // Single light sweep across the bar once it lands.
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.5) 50%, transparent 70%)",
      transform: "translateX(-110%)",
      animation: `${bloomSweep} 800ms ${BLOOM_EASE} 600ms both`,
      pointerEvents: "none",
    },
  },

  // Floats off the card's top-right corner (the corners themselves hold the
  // edition + status badges). Fades with .ef-cc.
  "& .ef-close": {
    position: "absolute",
    top: -10,
    right: -10,
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "1px solid rgba(204, 255, 153, 0.35)",
    background: "rgba(18, 18, 18, 0.7)",
    color: "#fff",
    fontSize: 11,
    lineHeight: 1,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    zIndex: 3,
  },

  // Reduced motion: every state switch is instant — no rings, ripple, morph,
  // stagger or sweep (the open rules above still apply their end values).
  "@media (prefers-reduced-motion: reduce)": {
    "& .ef-pulse": { animation: "none" },
    "& .ef-ripple.ef-go": { animation: "none" },
    "& .ef-bloom, & .ef-photo, & .ef-dot, & .ef-stem, & .ef-cc": { transition: "none" },
    // The open-state rules carry their own (higher-specificity) transitions.
    "&.ef-open .ef-bloom, &.ef-open .ef-photo, &.ef-open .ef-dot, &.ef-open .ef-stem": { transition: "none" },
    "&.ef-open .ef-cc": { animation: "none" },
    "&.ef-open .ef-cc .ef-cc-chip, &.ef-open .ef-cc .ef-cc-title, &.ef-open .ef-cc .ef-cc-stats, &.ef-open .ef-cc .ef-cc-sale":
      { animation: "none" },
    "&.ef-open .ef-cc .ef-cc-sale::after": { animation: "none" },
  },
}));

// Wraps a marker's MarkerBloomAnchor and pauses its idle pulse rings while the
// marker is panned off-screen. Subscribes the anchor element to the shared
// IntersectionObserver and flips local `offscreen` state from the callback —
// kept in React state (not an imperative classList toggle) so the markers memo
// re-rendering this anchor never wipes the `.ef-offscreen` pause class.
const PulseObservedAnchor = ({
  observer,
  callbacks,
  className,
  style,
  children,
}: {
  observer?: IntersectionObserver;
  callbacks: { current: WeakMap<Element, (offscreen: boolean) => void> };
  className: string;
  style: any;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [offscreen, setOffscreen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !observer) return undefined;
    callbacks.current.set(el, setOffscreen);
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      callbacks.current.delete(el);
    };
  }, [observer, callbacks]);
  return (
    <MarkerBloomAnchor ref={ref} className={offscreen ? `${className} ef-offscreen` : className} style={style}>
      {children}
    </MarkerBloomAnchor>
  );
};

export default Explore;
